import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import "App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "components/ui/sonner";

// Pages
import HomePage from "pages/HomePage";
import ShopPage from "pages/ShopPage";
import ProductPage from "pages/ProductPage";
import CartPage from "pages/CartPage";
import CheckoutPage from "pages/CheckoutPage";
import OrderConfirmationPage from "pages/OrderConfirmationPage";
import OrdersPage from "pages/OrdersPage";
import TrackOrderPage from "pages/TrackOrderPage";
import AdminDashboard from "pages/admin/AdminDashboard";
import AdminProducts from "pages/admin/AdminProducts";
import AdminOrders from "pages/admin/AdminOrders";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
const getCartSessionId = () => {
  let sessionId = localStorage.getItem('cart_session_id');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem('cart_session_id', sessionId);
  }
  return sessionId;
};
export const getCartConfig = (extra = {}) => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    ...extra,
    headers: {
      'X-Session-ID': getCartSessionId(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(extra.headers || {}),
    },
  };
};

// Helper to get auth headers (cookie OR localStorage token fallback for localhost)
export const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async (tokenOverride) => {
    try {
      const token = tokenOverride || localStorage.getItem('session_token');
      const config = {
        withCredentials: true,
        ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
      };
      const response = await axios.get(`${API}/auth/me`, config);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if Google redirected back with ?session_token= in the URL
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('session_token');
    if (urlToken) {
      localStorage.setItem('session_token', urlToken);
      // Remove token from URL without page reload
      window.history.replaceState({}, '', window.location.pathname);
      checkAuth(urlToken);
    } else {
      checkAuth();
    }
  }, []);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUrl}&response_type=code&scope=openid%20email%20profile`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, getAuthConfig());
      localStorage.removeItem('session_token');
      setUser(null);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Cart Context
const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const { user } = useAuth(); // React to auth changes

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`, getCartConfig());
      setCart(response.data);
    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  };

  // Re-fetch cart whenever auth state changes (login / logout)
  useEffect(() => { fetchCart(); }, [user]);

  const addToCart = async (productId, quantity = 1, size = null, color = null, variantId = null) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/cart/add`,
        { product_id: productId, quantity, size, color, variant_id: variantId },
        getCartConfig()
      );
      setCart(response.data);
      return true;
    } catch (error) {
      console.error("Error adding to cart:", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (productId, quantity, size = null, color = null, variantId = null) => {
    setLoading(true);
    try {
      const response = await axios.put(
        `${API}/cart/update`,
        null,
        getCartConfig({ params: { product_id: productId, quantity, size, color, variant_id: variantId } })
      );
      setCart(response.data);
    } catch (error) {
      console.error("Error updating cart:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId, size = null, color = null, variantId = null) => {
    setLoading(true);
    try {
      const response = await axios.delete(
        `${API}/cart/remove/${productId}`,
        getCartConfig({ params: { size, color, variant_id: variantId } })
      );
      setCart(response.data);
    } catch (error) {
      console.error("Error removing from cart:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart/clear`, getCartConfig());
      setCart({ items: [] });
    } catch (error) {
      console.error("Error clearing cart:", error);
    }
  };

  const cartItemCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const cartTotal = cart.items?.reduce((sum, item) => sum + item.price * item.quantity, 0) || 0;

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      addToCart,
      updateCartItem,
      removeFromCart,
      clearCart,
      fetchCart,
      cartItemCount,
      cartTotal
    }}>
      {children}
    </CartContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');

      if (code) {
        try {
          // We must use window.location (full browser navigation) to the backend
          // so the browser treats it as a same-site request and can receive the
          // Set-Cookie header. A cross-origin axios call drops SameSite=Lax cookies.
          window.location.href = `${API}/auth/callback?code=${encodeURIComponent(code)}`;
        } catch (error) {
          console.error("Auth callback error:", error);
          navigate('/', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFFCFA]">
      <div className="text-center">
        <div className="spinner mx-auto mb-4"></div>
        <p className="text-[#7D7D7D]">Signing you in...</p>
      </div>
    </div>
  );
};
// const AuthCallback = () => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const { setUser } = useAuth();
//   const hasProcessed = useRef(false);

//   useEffect(() => {
//     if (hasProcessed.current) return;
//     hasProcessed.current = true;

//     const processAuth = async () => {
//       const hash = location.hash;
//       const sessionIdMatch = hash.match(/session_id=([^&]+)/);

//       if (sessionIdMatch) {
//         const sessionId = sessionIdMatch[1];
//         try {
//           const response = await axios.get(
//             `${API}/auth/session?session_id=${sessionId}`,
//             { withCredentials: true }
//           );
//           setUser(response.data);
//           navigate('/', { replace: true });
//         } catch (error) {
//           console.error("Auth callback error:", error);
//           navigate('/', { replace: true });
//         }
//       } else {
//         navigate('/', { replace: true });
//       }
//     };

//     processAuth();
//   }, []);

//   return (
//     <div className="min-h-screen flex items-center justify-center bg-[#FFFCFA]">
//       <div className="text-center">
//         <div className="spinner mx-auto mb-4"></div>
//         <p className="text-[#7D7D7D]">Signing you in...</p>
//       </div>
//     </div>
//   );
// };

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, login } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFCFA]">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFCFA]">
        <div className="text-center">
          <h2 className="font-serif text-2xl mb-4">Please Sign In</h2>
          <p className="text-[#7D7D7D] mb-6">You need to be signed in to access this page.</p>
          <button onClick={login} className="btn-primary" data-testid="sign-in-btn">
            Sign In with Google
          </button>
        </div>
      </div>
    );
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Check for session_id in URL hash synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/shop/:category" element={<ShopPage />} />
      <Route path="/product/:productId" element={<ProductPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
      <Route path="/orders" element={
        <ProtectedRoute>
          <OrdersPage />
        </ProtectedRoute>
      } />
      <Route path="/track" element={<TrackOrderPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/products" element={
        <ProtectedRoute adminOnly>
          <AdminProducts />
        </ProtectedRoute>
      } />
      <Route path="/admin/orders" element={
        <ProtectedRoute adminOnly>
          <AdminOrders />
        </ProtectedRoute>
      } />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
